/**
 * Page-top chrome — wordmark, autonomy state pill, Halt the loop.
 *
 * Phase 2 of the frontend redesign — see
 * docs/plans/2026-04-28-frontend-redesign.md §2 (the shape) and §5.1 (Posture).
 */

import { Shield } from './Shield'
import { HaltTheLoop } from './HaltTheLoop'
import { ThemeToggle } from './ThemeToggle'
import { TimeTravelPicker } from './TimeTravelPicker'
import { postureVerb } from '../posture/lib'
import { useTradingStore } from '../../state/trading'
import type { AutonomyReadiness, TradingMode } from '../../types/system'

interface PillCopy {
  label: string
  className: string
}

function approvalLabel(readiness: AutonomyReadiness | undefined): string | null {
  if (!readiness) return null
  if (!readiness.profileAutonomyEnabled || readiness.approvalMode === 'full_manual') return 'Manual'
  if (readiness.approvalMode === 'semi_auto') return 'Review'
  if (readiness.approvalMode === 'auto_veto') return 'Auto veto'
  return 'Auto'
}

function pillCopy(
  mode: TradingMode,
  readiness: AutonomyReadiness | undefined,
  blockers: string[] | undefined,
): PillCopy {
  if (mode === 'unknown') return { label: 'Mode unknown', className: 'border-risk text-risk' }
  if (mode === 'halted') return { label: 'Halted', className: 'border-risk text-risk' }
  const modeLabel = mode === 'live' ? 'Live' : 'Paper'
  if (blockers && blockers.length > 0) {
    return { label: `Gated · ${modeLabel}`, className: 'border-risk text-risk' }
  }
  const approval = approvalLabel(readiness)
  if (readiness) {
    const posture = postureVerb(readiness)
    if (posture.semantic === 'halted' || posture.semantic === 'gated') {
      return {
        label: `${posture.word.replace('.', '')} · ${modeLabel}`,
        className:
          posture.semantic === 'halted'
            ? 'border-risk text-risk'
            : posture.semantic === 'gated'
              ? 'border-amber text-amber'
              : 'border-rule-2 text-muted',
      }
    }
  }
  if (approval === 'Manual') {
    return {
      label: `Manual · ${modeLabel}`,
      className: 'border-rule-2 text-muted',
    }
  }
  if (!approval) {
    return {
      label: `Unknown · ${modeLabel}`,
      className: 'border-rule-2 text-muted',
    }
  }
  const className =
    approval === 'Manual'
      ? 'border-rule-2 text-muted'
      : mode === 'live'
        ? 'border-capital text-capital'
        : 'border-amber text-amber'
  if (mode === 'live')
    return {
      label: `${approval} · Live`,
      className,
    }
  return {
    label: `${approval} · Paper`,
    className,
  }
}

export function HeaderChrome() {
  const system = useTradingStore((s) => s.system)
  const tradingMode = system?.tradingMode ?? 'unknown'
  const pill = pillCopy(tradingMode, system?.autonomyReadiness, system?.blockers)

  return (
    <header
      role="banner"
      aria-label="Aegis chrome"
      className="flex items-center justify-between gap-4 border-b border-rule px-8 py-4"
    >
      {/* Left: wordmark + autonomy state */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-3">
          <Shield />
          <span className="font-mono text-xs font-semibold tracking-[0.18em] text-bone">AEGIS</span>
        </div>
        <div
          className={`flex items-center gap-2 border px-2.5 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.1em] ${pill.className}`}
          data-testid="autonomy-state-pill"
        >
          <span
            aria-hidden="true"
            className={`h-1.5 w-1.5 rounded-full ${
              pill.className.includes('amber')
                ? 'bg-amber'
                : pill.className.includes('capital')
                  ? 'bg-capital'
                  : pill.className.includes('risk')
                    ? 'bg-risk'
                    : 'bg-muted'
            }`}
          />
          {pill.label}
        </div>
      </div>

      {/* Right: time-travel + theme toggle + audited kill switch (desktop only). */}
      <div className="flex items-center gap-2">
        <TimeTravelPicker />
        <ThemeToggle />
        <div className="hidden sm:block">
          <HaltTheLoop />
        </div>
      </div>
    </header>
  )
}
